
import { ReactNode, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdministrationAPI } from "../AdministrationAPI";
import Section from "../components/Section";
import { useModalContext } from "../components/ModalProvider";
import { Invoice, InvoiceStatus } from "@the-real-insight/tri-model";
import dayjs from "dayjs";
import { useFormats } from "../hooks/formats";
import { pdf } from "@react-pdf/renderer";
import { downloadFile, PDFInvoice } from "../billing/invoice";
import { useUrlPageState } from "../utilities/dataTablePagination";
import { legalPersonAddressFields, legalPersonCompanyName } from "../utilities/legalPersonDisplay";

import type { Column, ColumnDef } from "@tanstack/react-table";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Calendar as CalendarIcon,
    Check,
    FileText,
    Loader2,
    RefreshCw,
    Trash2,
    Undo2,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { DataTable } from "@/components/ui/data-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";

/** Anzeigeformat des abgelösten Fluent-DatePickers (M/D/YYYY) — unverändert. */
const DATE_DISPLAY_FORMAT = 'M/D/YYYY';

/** Klickbarer Spaltenkopf — ersetzt die Sortierung der abgelösten Tabelle. */
function SortableHeader({
    column,
    align,
    children,
}: {
    column: Column<any, unknown>;
    align?: 'right';
    children: ReactNode;
}) {
    const sorted = column.getIsSorted();

    return (
        <div className={align === 'right' ? 'flex justify-end' : undefined}>
            <Button
                variant="ghost"
                size="sm"
                className="-mx-3 h-8 gap-1 px-3 font-bold text-foreground"
                onClick={() => column.toggleSorting(sorted === "asc")}
            >
                {children}
                {sorted === "asc" ? (
                    <ArrowUp aria-hidden className="h-3.5 w-3.5" />
                ) : sorted === "desc" ? (
                    <ArrowDown aria-hidden className="h-3.5 w-3.5" />
                ) : (
                    <ArrowUpDown aria-hidden className="h-3.5 w-3.5 opacity-50" />
                )}
            </Button>
        </div>
    );
}

/** Leerzustand einer Tabelle: Überschrift, Erklärung und Handlungsaufforderung. */
function TableEmptyState({
    title,
    hint,
    action,
}: {
    title: string;
    hint?: string;
    action?: ReactNode;
}) {
    return (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
            <div className="text-sm font-semibold text-foreground">{title}</div>
            {hint ? <div className="max-w-prose text-sm text-muted-foreground">{hint}</div> : null}
            {action}
        </div>
    );
}

/** Datumsfeld: Kalender (react-day-picker) in einem Popover, Anzeige über dayjs. */
function DateField({
    id,
    label,
    value,
    className,
    onSelectDate,
}: {
    id: string;
    label: string;
    value?: Date;
    className?: string;
    onSelectDate: (date: Date) => void;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const selected = value && !Number.isNaN(value.getTime()) ? value : undefined;

    return (
        <div className={cn('flex min-w-0 flex-col gap-1.5', className)}>
            <Label htmlFor={id}>{label}</Label>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        id={id}
                        type="button"
                        variant="outline"
                        className="w-full justify-between font-normal"
                    >
                        <span className={cn('truncate', !selected && 'text-muted-foreground')}>
                            {selected ? dayjs(selected).format(DATE_DISPLAY_FORMAT) : t('global.pleaseSelect')}
                        </span>
                        <CalendarIcon aria-hidden className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={selected}
                        defaultMonth={selected}
                        onSelect={(date) => {
                            if (date) {
                                onSelectDate(date);
                            }
                            setOpen(false);
                        }}
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}

const invoiceKey = (invoice: any) => String(invoice?.id ?? invoice?._id ?? '');
const customerKey = (customer: any) => String(customer?.id ?? customer?._id ?? '');

export default function BillingPanel(_properties: any) {
    const { t } = useTranslation();
    const fieldId = useId();
    const invoiceListPagination = useUrlPageState('invoiceListPage');
    const customerListPagination = useUrlPageState('customerListPage');
    const { pushError, pushSuccess, showConfirmationDialog } = useModalContext() as any;
    const { currencyFormat } = useFormats();
    const { getCustomers, runBilling, runBillingGlobal, getAllInvoices, updateInvoices, deleteInvoice } = useAdministrationAPI();
    const [customerFilter, setCustomerFilter] = useState() as any;
    const [customers, setCustomers] = useState([]);
    const [customersLoading, setCustomersLoading] = useState(true);
    const [period, setPeriod] = useState(new Date());
    const [selectedPeriod, setSelectedPeriod] = useState(new Date());
    const [selectedCustomerKeys, setSelectedCustomerKeys] = useState<Set<string>>(new Set());
    const [runningGlobalBilling, setRunningGlobalBilling] = useState(false);
    const [runningBilling, setRunningBilling] = useState(false);

    // Invoice state
    const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
    const [invoicesLoading, setInvoicesLoading] = useState(false);
    const [invoiceFromDate, setInvoiceFromDate] = useState(dayjs().subtract(6, 'month').startOf('month').toDate());
    const [invoiceToDate, setInvoiceToDate] = useState(dayjs().endOf('month').toDate());
    const [invoiceOpenOnly, setInvoiceOpenOnly] = useState(false);
    const [invoiceFilter, setInvoiceFilter] = useState("");
    const [selectedInvoiceKeys, setSelectedInvoiceKeys] = useState<Set<string>>(new Set());
    const [marking, setMarking] = useState(false);

    const openOnlyId = `${fieldId}-open-only`;
    const invoiceFilterId = `${fieldId}-invoice-filter`;
    const customerFilterId = `${fieldId}-customer-filter`;

    const selectedInvoices: Invoice[] = allInvoices.filter(
        (invoice: Invoice) => selectedInvoiceKeys.has(invoiceKey(invoice))
    );
    const selectedCustomers: any[] = customers.filter(
        (customer: any) => selectedCustomerKeys.has(customerKey(customer))
    );

    const filterItem = (filter: string, item: any, fields: any) => {
        if (!filter || filter.trim().length === 0) {
            return true;
        }

        for (const field of fields) {
            if (item[field] &&
                item[field].toLowerCase().indexOf(filter.toLowerCase()) === 0) {
                return true;
            }
        }

        return false;
    };

    const filterCustomers = async () => {
        setCustomersLoading(true);
        try {
            const allCustomers = await getCustomers();

            setCustomers(allCustomers.filter((customer: any) => filterItem(customerFilter, customer, ['name', 'domain'])));
        } finally {
            setCustomersLoading(false);
        }
    };

    const loadInvoices = async () => {
        setInvoicesLoading(true);
        try {
            const invoices = await getAllInvoices(invoiceFromDate, invoiceToDate, invoiceOpenOnly, invoiceFilter);
            setAllInvoices(invoices || []);
        } catch (error) {
            pushError(error);
        } finally {
            setInvoicesLoading(false);
        }
    };

    useEffect(() => {
        filterCustomers();
    }, [customerFilter]);

    useEffect(() => {
        loadInvoices();
    }, [invoiceFromDate, invoiceToDate, invoiceOpenOnly, invoiceFilter]);

    const customerColumns: ColumnDef<any>[] = [{
        id: 'select',
        size: 48,
        enableSorting: false,
        header: () => {
            const selectableKeys = customers.map((customer: any) => customerKey(customer));
            const allSelected =
                selectableKeys.length > 0 && selectableKeys.every((key: string) => selectedCustomerKeys.has(key));

            return (
                <Checkbox
                    checked={allSelected}
                    aria-label={t('administrationPage.billingPanel.selectAllCustomers')}
                    onCheckedChange={(checked) =>
                        setSelectedCustomerKeys(checked === true ? new Set<string>(selectableKeys) : new Set<string>())
                    }
                />
            );
        },
        cell: ({ row }) => {
            const key = customerKey(row.original);

            return (
                <Checkbox
                    checked={selectedCustomerKeys.has(key)}
                    aria-label={`${t('global.select')}: ${row.original.name || key}`}
                    onCheckedChange={(checked) => {
                        const keys = new Set(selectedCustomerKeys);
                        if (checked === true) {
                            keys.add(key);
                        } else {
                            keys.delete(key);
                        }
                        setSelectedCustomerKeys(keys);
                    }}
                />
            );
        },
    }, {
        id: 'id',
        size: 150,
        enableSorting: false,
        header: () => 'ID',
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original.id}</span>,
    }, {
        id: 'name',
        size: 250,
        enableSorting: false,
        header: () => 'Name',
        cell: ({ row }) => <span className="min-w-0 break-words text-sm">{row.original.name}</span>,
    }, {
        id: 'created',
        size: 100,
        enableSorting: false,
        header: () => <div className="text-right">Created</div>,
        cell: ({ row }) => (
            <div className="whitespace-nowrap text-right text-sm">{(() => {
                const d = new Date(row.original.created);
                return isNaN(d.getTime()) ? '-' : d.toLocaleDateString();
            })()}</div>
        ),
    }, {
        id: 'domain',
        size: 250,
        enableSorting: false,
        header: () => 'Domain',
        cell: ({ row }) => (
            <a
                className="min-w-0 break-all text-sm underline underline-offset-2"
                href={`https://${row.original.domain}`}
                target="_blank"
                rel="noreferrer"
            >
                {row.original.domain}
            </a>
        ),
    }];

    const invoiceColumns: ColumnDef<any>[] = [
        {
            id: 'select',
            size: 48,
            enableSorting: false,
            header: () => {
                const selectableKeys = allInvoices.map((invoice: Invoice) => invoiceKey(invoice));
                const allSelected =
                    selectableKeys.length > 0 && selectableKeys.every((key: string) => selectedInvoiceKeys.has(key));

                return (
                    <Checkbox
                        checked={allSelected}
                        aria-label={t('administrationPage.billingPanel.selectAllInvoices')}
                        onCheckedChange={(checked) =>
                            setSelectedInvoiceKeys(checked === true ? new Set<string>(selectableKeys) : new Set<string>())
                        }
                    />
                );
            },
            cell: ({ row }) => {
                const key = invoiceKey(row.original);

                return (
                    <Checkbox
                        checked={selectedInvoiceKeys.has(key)}
                        aria-label={`${t('global.select')}: ${row.original.id}`}
                        onCheckedChange={(checked) => {
                            const keys = new Set(selectedInvoiceKeys);
                            if (checked === true) {
                                keys.add(key);
                            } else {
                                keys.delete(key);
                            }
                            setSelectedInvoiceKeys(keys);
                        }}
                    />
                );
            },
        },
        {
            id: 'paymentStatus',
            size: 100,
            accessorFn: (invoice: Invoice) => (invoice.status === InvoiceStatus.paid ? 1 : 0),
            header: ({ column }) => (
                <SortableHeader column={column}>{t("billingPanel.paymentStatusColumn")}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div
                    className={
                        (row.original.status === InvoiceStatus.paid
                            ? "text-success"
                            : "text-destructive") + " text-sm font-bold"
                    }
                >
                    {row.original.status === InvoiceStatus.paid ? t("billingPanel.paid") : t("billingPanel.open")}
                </div>
            ),
        },
        {
            id: 'buyer',
            size: 180,
            accessorFn: (invoice: Invoice) => (invoice.buyer?.name || "-").toLowerCase(),
            header: ({ column }) => (
                <SortableHeader column={column}>{t("billingPanel.buyerColumn")}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 break-words text-sm">
                    {row.original.buyer ? (
                        <div>
                            <div>{row.original.buyer.name}</div>
                            <div>{row.original.buyer.address?.city}</div>
                        </div>
                    ) : (
                        <div>-</div>
                    )}
                </div>
            ),
        },
        {
            id: 'provider',
            size: 180,
            accessorFn: (invoice: Invoice) =>
                (invoice.seller ? legalPersonCompanyName(invoice.seller as any) : t("billingPanel.internal")).toLowerCase(),
            header: ({ column }) => (
                <SortableHeader column={column}>{t("billingPanel.providerColumn")}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 break-words text-sm">
                    {row.original.seller ? (
                        <div>
                            <div>{legalPersonCompanyName(row.original.seller as any)}</div>
                            <div>{legalPersonAddressFields(row.original.seller as any).city}</div>
                        </div>
                    ) : (
                        <div>{t("billingPanel.internal")}</div>
                    )}
                </div>
            ),
        },
        {
            id: 'date',
            size: 110,
            accessorFn: (invoice: Invoice) => (invoice.creationDate ? new Date(invoice.creationDate).getTime() : 0),
            header: ({ column }) => (
                <SortableHeader column={column} align="right">{t("billingPanel.dateColumn")}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div className="whitespace-nowrap text-right text-sm">
                    {dayjs(row.original.creationDate).format(t("global.dateShort"))}
                </div>
            ),
        },
        {
            id: 'period',
            size: 150,
            accessorFn: (invoice: Invoice) => (invoice.periodFromDate ? new Date(invoice.periodFromDate).getTime() : 0),
            header: ({ column }) => (
                <SortableHeader column={column}>{t("billingPanel.periodColumn")}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div className="whitespace-nowrap text-sm">
                    {dayjs(row.original.periodFromDate).format(t("global.dateShort"))} -{" "}
                    {dayjs(new Date(row.original.periodToDate)).format(t("global.dateShort"))}
                </div>
            ),
        },
        {
            id: 'product',
            size: 200,
            accessorFn: (invoice: Invoice) => {
                const names = (invoice.itemGroups || [])
                    .map((itemGroup: any) => itemGroup.name || "")
                    .join(" ");
                return names.toLowerCase();
            },
            header: ({ column }) => (
                <SortableHeader column={column}>{t("billingPanel.productColumn")}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div className="min-w-0 break-words text-sm">
                    {row.original.itemGroups?.map((itemGroup: any, index: number) => (
                        <div key={index}>{itemGroup.name}</div>
                    ))}
                </div>
            ),
        },
        {
            id: 'amount',
            size: 130,
            accessorFn: (invoice: Invoice) => invoice.subtotal(),
            header: ({ column }) => (
                <SortableHeader column={column} align="right">{t("billingPanel.amountColumn")}</SortableHeader>
            ),
            cell: ({ row }) => (
                <div className="whitespace-nowrap text-right text-sm tabular-nums">
                    {currencyFormat.format(row.original.subtotal())}
                </div>
            ),
        },
        {
            id: 'actions',
            size: 110,
            enableSorting: false,
            header: () => <span className="sr-only">{t('administrationPage.billingPanel.actionsColumn')}</span>,
            cell: ({ row }) => (
                <div className="flex gap-1">
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t("billingPanel.downloadInvoiceForecastPDF")}
                                    onClick={async () => {
                                        // Create a minimal customer object with the ID from the invoice
                                        const customerForPdf = { id: row.original.customer } as any;
                                        const blob = await pdf(
                                            <PDFInvoice
                                                invoice={row.original}
                                                customer={customerForPdf}
                                            ></PDFInvoice>
                                        ).toBlob();

                                        downloadFile(blob, `${t("billingPanel.invoice")} ${row.original.id}`);
                                    }}
                                >
                                    <FileText aria-hidden className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("billingPanel.downloadInvoiceForecastPDF")}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={t("billingPanel.deleteInvoice")}
                                    onClick={() => handleDeleteInvoice(row.original)}
                                >
                                    <Trash2 aria-hidden className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>{t("billingPanel.deleteInvoice")}</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            ),
        },
    ];

    const handleMarkAsPaid = async () => {
        if (selectedInvoices.length === 0) return;

        const count = selectedInvoices.length;

        setMarking(true);

        try {
            const updatedInvoices = selectedInvoices.map(invoice => ({
                ...invoice,
                status: InvoiceStatus.paid,
                paidDate: new Date()
            }));

            await updateInvoices(updatedInvoices as Invoice[]);
            setSelectedInvoiceKeys(new Set());
            await loadInvoices();
            pushSuccess(t('administrationPage.billingPanel.markedPaid', { count }));
        } catch (error) {
            pushError(error);
        } finally {
            setMarking(false);
        }
    };

    const handleMarkAsOpen = async () => {
        if (selectedInvoices.length === 0) return;

        const count = selectedInvoices.length;

        setMarking(true);

        try {
            const updatedInvoices = selectedInvoices.map(invoice => ({
                ...invoice,
                status: InvoiceStatus.open,
                paidDate: undefined
            }));

            await updateInvoices(updatedInvoices as Invoice[]);
            setSelectedInvoiceKeys(new Set());
            await loadInvoices();
            pushSuccess(t('administrationPage.billingPanel.markedOpen', { count }));
        } catch (error) {
            pushError(error);
        } finally {
            setMarking(false);
        }
    };

    const handleDeleteInvoice = async (invoice: Invoice) => {
        showConfirmationDialog(
            t("billingPanel.deleteInvoice"),
            t("billingPanel.deleteInvoiceConfirmation", { invoiceId: invoice.id }),
            async () => {
                try {
                    await deleteInvoice(invoice.id);
                    await loadInvoices();
                    pushSuccess(t('administrationPage.billingPanel.invoiceDeleted', { invoiceId: invoice.id }));
                } catch (error) {
                    pushError(error);
                }
            }
        );
    };

    // Calculate totals
    const totalAmount = allInvoices.reduce((sum, inv) => sum + inv.subtotal(), 0);
    const openAmount = allInvoices
        .filter(inv => inv.status !== InvoiceStatus.paid)
        .reduce((sum, inv) => sum + inv.subtotal(), 0);
    const paidAmount = allInvoices
        .filter(inv => inv.status === InvoiceStatus.paid)
        .reduce((sum, inv) => sum + inv.subtotal(), 0);

    const invoiceFilterActive = invoiceOpenOnly || invoiceFilter.trim().length > 0;
    const customerFilterActive = Boolean(customerFilter && String(customerFilter).trim().length > 0);

    return <>
        <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-6">
            <Section title={t("billingPanel.allInvoices")}>
                <div className="flex min-w-0 flex-wrap items-end gap-4">
                    <DateField
                        id={`${fieldId}-invoice-from`}
                        className="w-[160px]"
                        label={t("billingPanel.fromDate")}
                        value={invoiceFromDate}
                        onSelectDate={setInvoiceFromDate}
                    />
                    <DateField
                        id={`${fieldId}-invoice-to`}
                        className="w-[160px]"
                        label={t("billingPanel.toDate")}
                        value={invoiceToDate}
                        onSelectDate={setInvoiceToDate}
                    />
                    <div className="flex items-center gap-2 pb-2">
                        <Checkbox
                            id={openOnlyId}
                            checked={invoiceOpenOnly}
                            onCheckedChange={(checked) => setInvoiceOpenOnly(checked === true)}
                        />
                        <Label htmlFor={openOnlyId}>{t("billingPanel.openOnly")}</Label>
                    </div>
                    <div className="flex w-[200px] min-w-0 max-w-full flex-col gap-1.5">
                        <Label htmlFor={invoiceFilterId}>{t("billingPanel.filter")}</Label>
                        <Input
                            id={invoiceFilterId}
                            value={invoiceFilter}
                            onChange={(event) => setInvoiceFilter(event.target.value || "")}
                        />
                    </div>
                    <Button variant="outline" className="max-sm:w-full" onClick={loadInvoices} disabled={invoicesLoading}>
                        {invoicesLoading
                            ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />
                            : <RefreshCw aria-hidden className="mr-2 h-4 w-4" />}
                        {t("global.refresh")}
                    </Button>
                </div>
                <dl className="mt-4 flex min-w-0 flex-wrap gap-x-8 gap-y-2 text-sm">
                    <div className="flex items-center gap-2">
                        <dt className="font-bold">{t("billingPanel.totalInvoices")}:</dt>
                        <dd className="tabular-nums">{allInvoices.length}</dd>
                    </div>
                    <div className="flex items-center gap-2">
                        <dt className="font-bold">{t("billingPanel.totalAmount")}:</dt>
                        <dd className="tabular-nums">{currencyFormat.format(totalAmount)}</dd>
                    </div>
                    <div className="flex items-center gap-2">
                        <dt className="font-bold text-success">{t("billingPanel.paidAmount")}:</dt>
                        <dd className="tabular-nums text-success">{currencyFormat.format(paidAmount)}</dd>
                    </div>
                    <div className="flex items-center gap-2">
                        <dt className="font-bold text-destructive">{t("billingPanel.openAmount")}:</dt>
                        <dd className="tabular-nums text-destructive">{currencyFormat.format(openAmount)}</dd>
                    </div>
                </dl>
                <div className="mt-4 flex min-w-0 flex-wrap items-center gap-2">
                    <Button
                        className="max-sm:w-full"
                        disabled={marking || selectedInvoices.length === 0 || selectedInvoices.every(inv => inv.status === InvoiceStatus.paid)}
                        onClick={handleMarkAsPaid}
                    >
                        {marking
                            ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />
                            : <Check aria-hidden className="mr-2 h-4 w-4" />}
                        {t("billingPanel.markAsPaid")}
                    </Button>
                    <Button
                        variant="outline"
                        className="max-sm:w-full"
                        disabled={marking || selectedInvoices.length === 0 || selectedInvoices.every(inv => inv.status !== InvoiceStatus.paid)}
                        onClick={handleMarkAsOpen}
                    >
                        <Undo2 aria-hidden className="mr-2 h-4 w-4" />
                        {t("billingPanel.markAsOpen")}
                    </Button>
                    {selectedInvoices.length > 0 && (
                        <span className="text-sm text-muted-foreground">
                            {selectedInvoices.length} {t("billingPanel.selected")}
                        </span>
                    )}
                </div>
                <div className="mt-4 min-w-0 overflow-x-auto">
                    <DataTable
                        columns={invoiceColumns}
                        data={allInvoices}
                        loading={invoicesLoading}
                        pageSize={10}
                        page={invoiceListPagination.page}
                        onPageChange={invoiceListPagination.onPageChange}
                        previousAriaLabel={t('global.back')}
                        nextAriaLabel={t('global.next')}
                        emptyMessage={
                            invoiceFilterActive ? (
                                <TableEmptyState
                                    title={t('administrationPage.billingPanel.noMatchingInvoicesTitle')}
                                    hint={t('administrationPage.billingPanel.noMatchingInvoicesHint')}
                                    action={
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                                setInvoiceOpenOnly(false);
                                                setInvoiceFilter('');
                                            }}
                                        >
                                            {t('billingPanel.resetFilter')}
                                        </Button>
                                    }
                                />
                            ) : (
                                <TableEmptyState
                                    title={t('administrationPage.billingPanel.noInvoicesTitle')}
                                    hint={t('administrationPage.billingPanel.noInvoicesHint')}
                                />
                            )
                        }
                    />
                </div>
            </Section>
            <Section title="Global Billing">
                <div className="flex min-w-0 flex-wrap items-end gap-4">
                    <DateField
                        id={`${fieldId}-global-period`}
                        className="w-[220px]"
                        label="Run billing for all customers for period"
                        value={period}
                        onSelectDate={setPeriod}
                    />
                    <Button
                        className="max-sm:w-full"
                        disabled={runningGlobalBilling}
                        onClick={async () => {
                            setRunningGlobalBilling(true);
                            try {
                                console.log(await runBillingGlobal(new Date(period)));
                                pushSuccess(t('administrationPage.billingPanel.globalBillingStarted'));
                            } catch (error) {
                                pushError(error);
                            } finally {
                                setRunningGlobalBilling(false);
                            }
                        }}
                    >
                        {runningGlobalBilling && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                        Run global Billing
                    </Button>
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                    Billing run is reentrant. If called multiple times for the same period it is a no-op.
                </p>
            </Section>
            <Section title="Customers">
                <div className="flex min-w-0 flex-wrap items-end gap-4">
                    <div className="flex w-[200px] min-w-0 max-w-full flex-col gap-1.5">
                        <Label htmlFor={customerFilterId}>Filter</Label>
                        <Input
                            id={customerFilterId}
                            value={customerFilter || ''}
                            onChange={(event) => setCustomerFilter(event.target.value)}
                        />
                    </div>
                    <div className="flex flex-1 flex-wrap items-end justify-end gap-4">
                        <DateField
                            id={`${fieldId}-customer-period`}
                            className="w-[220px]"
                            label="Billing period"
                            value={selectedPeriod}
                            onSelectDate={setSelectedPeriod}
                        />
                        <Button
                            className="max-sm:w-full"
                            disabled={runningBilling || selectedCustomers.length === 0}
                            onClick={async () => {
                                setRunningBilling(true);
                                try {
                                    console.log(await runBilling(selectedCustomers, new Date(selectedPeriod)));
                                    pushSuccess(t('administrationPage.billingPanel.billingStarted', {
                                        count: selectedCustomers.length,
                                    }));
                                } catch (error) {
                                    pushError(error);
                                } finally {
                                    setRunningBilling(false);
                                }
                            }}
                        >
                            {runningBilling && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
                            Run Billing
                        </Button>
                    </div>
                </div>
                <div className="mt-4 min-w-0 overflow-x-auto">
                    <DataTable
                        columns={customerColumns}
                        data={customers}
                        loading={customersLoading}
                        pageSize={10}
                        page={customerListPagination.page}
                        onPageChange={customerListPagination.onPageChange}
                        previousAriaLabel={t('global.back')}
                        nextAriaLabel={t('global.next')}
                        emptyMessage={
                            customerFilterActive ? (
                                <TableEmptyState
                                    title={t('administrationPage.billingPanel.noMatchingCustomersTitle')}
                                    hint={t('administrationPage.billingPanel.noMatchingCustomersHint')}
                                    action={
                                        <Button variant="outline" size="sm" onClick={() => setCustomerFilter('')}>
                                            {t('billingPanel.resetFilter')}
                                        </Button>
                                    }
                                />
                            ) : (
                                <TableEmptyState
                                    title={t('administrationPage.billingPanel.noCustomersTitle')}
                                    hint={t('administrationPage.billingPanel.noCustomersHint')}
                                />
                            )
                        }
                    />
                </div>
            </Section>
        </div>
    </>;
}
